import { leaseCompanyMst } from '@libs/domain';
import type { FieldErrorField } from '@ps-smart-cp/ppfw-client';
import { FieldSet, TextNumberField, kebabCase, useItemName, useValidator } from '@ps-smart-cp/ppfw-client';
import type { Ref } from 'react';
import { forwardRef, useImperativeHandle, useState } from 'react';
import { useMstSettings, useOperationControl } from '../../../context';
import { leaseCompanyMstSettingFieldConfig } from '../item-config';
import { registerSetting } from '../repositories';

interface LeaseCompanySettingFieldSetProps {
  errorFields?: string[];
}

interface LeaseCompanySettingFieldSetRef {
  validate: () => FieldErrorField[];
  register: () => Promise<void>;
}

function InnerComponent(props: LeaseCompanySettingFieldSetProps, ref: Ref<LeaseCompanySettingFieldSetRef>) {
  const getName = useItemName();
  const { leaseCompany } = useMstSettings();
  const [setting, setSetting] = useState(leaseCompany);
  const validator = useValidator();
  const validate = () =>
    validator
      .with(setting, leaseCompanyMstSettingFieldConfig)
      .execute()
      .map((s) => ({ ...s, field: kebabCase(`lease-company-${s.field}`) }));
  const register = async () => registerSetting(setting);
  const { isStarted } = useOperationControl();

  useImperativeHandle(ref, () => ({ validate, register }), [setting]);

  return (
    <FieldSet
      legendProps={{ title: getName(leaseCompanyMst.names.laCompany), id: 'lease-company-field-set' }}
    >
      <TextNumberField
        id="lease-company-code-digits"
        obj={setting}
        setObj={setSetting}
        prop="codeDigits"
        items={leaseCompanyMstSettingFieldConfig}
        errorFields={props.errorFields}
        disabled={isStarted}
      />
    </FieldSet>
  );
}

export const LeaseCompanySettingFieldSet = forwardRef(InnerComponent);
export type { LeaseCompanySettingFieldSetRef };
